import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Pencil, Trash2, FileText, Upload, Factory } from 'lucide-react';
import { fornitori as fornitoriApi, ordini as ordiniApi, cantieri as cantieriApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import UploadDocumentoModal from '../components/documenti/UploadDocumentoModal';
import useIsMobile from '../hooks/useIsMobile';
import { useListMode, ListModeToggle } from '../components/shared/ListView';
import DataCard from '../components/shared/DataCard';
import RankingTable, { renderBarraCoda } from '../components/shared/RankingTable';
import NomeGiaPresenteModal from '../components/shared/NomeGiaPresenteModal';
import { nomeGiaPresenteDaErrore, stessoNome } from '../components/shared/nomeGiaPresente';

const fmtEur = (n) => n == null ? '—' : `€ ${Number(n).toLocaleString('it-IT', { minimumFractionDigits: 0 })}`;

// Helper visivi condivisi (design system)
const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const ROW_BG = (i) => (i % 2 === 0 ? 'var(--ds-bg)' : 'var(--chip)');
const ROW_HOVER = 'color-mix(in srgb, var(--brand) 9%, var(--ds-bg))';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

// Categoria → token zona/semantico (badge bg via color-mix, niente alpha-concat)
const CATEGORIA_META = {
  materiali_costruzione: { label: 'Materiali costruzione', color: 'var(--warn)' },
  componenti_elettrici:  { label: 'Componenti elettrici',  color: 'var(--zone-blue)' },
  idraulica:             { label: 'Idraulica',             color: 'var(--ok)' },
  legno_infissi:         { label: 'Legno e infissi',       color: 'var(--zone-violet)' },
  piastrelle_ceramiche:  { label: 'Piastrelle',            color: 'var(--ds-danger)' },
  ferramenta:            { label: 'Ferramenta',            color: 'var(--zone-slate)' },
  noleggio:              { label: 'Noleggio',              color: 'var(--zone-magenta)' },
  trasporti:             { label: 'Trasporti',             color: 'var(--zone-acqua)' },
  subappalto:            { label: 'Subappalto',            color: 'var(--brand)' },
  altro:                 { label: 'Altro',                 color: 'var(--ds-muted)' },
};
const CATEGORIE_LIST = Object.keys(CATEGORIA_META);

const FORM_EMPTY = {
  nome: '', email: '', telefono: '', indirizzo: '', citta: '',
  provincia: '', piva: '', sito_web: '', nota: '',
  categoria: 'altro', stato: 'attivo',
};

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px', borderBottom: '1px solid var(--ds-border)' }}>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)' }}>{title}</h2>
          <button onClick={onClose} aria-label="Chiudi" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--ds-muted)', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        <div style={{ padding: '24px 28px' }}>{children}</div>
      </div>
    </div>
  );
}

function FornitoreForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState({ ...FORM_EMPTY, ...initial });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' };
  const lbl = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4 };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 };
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <div style={{ marginBottom: 16 }}><label style={lbl}>Nome / Ragione sociale *</label>
        <input required data-campo-nome="true" style={inp} value={form.nome} onChange={(e) => set('nome', e.target.value)} /></div>
      <div style={row2}>
        <div><label style={lbl}>Categoria</label>
          <select style={inp} value={form.categoria} onChange={(e) => set('categoria', e.target.value)}>
            {CATEGORIE_LIST.map((c) => <option key={c} value={c}>{CATEGORIA_META[c].label}</option>)}
          </select></div>
        <div><label style={lbl}>Stato</label>
          <select style={inp} value={form.stato} onChange={(e) => set('stato', e.target.value)}>
            <option value="attivo">Attivo</option>
            <option value="non_attivo">Non attivo</option>
          </select></div>
      </div>
      <div style={row2}>
        <div><label style={lbl}>Email</label><input style={inp} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
        <div><label style={lbl}>Telefono</label><input style={inp} value={form.telefono} onChange={(e) => set('telefono', e.target.value)} /></div>
      </div>
      <div style={row2}>
        <div><label style={lbl}>Partita IVA</label><input style={inp} value={form.piva} onChange={(e) => set('piva', e.target.value)} /></div>
        <div><label style={lbl}>Sito web</label><input style={inp} type="url" value={form.sito_web} onChange={(e) => set('sito_web', e.target.value)} placeholder="https://…" /></div>
      </div>
      <div style={{ marginBottom: 16 }}><label style={lbl}>Indirizzo</label><input style={inp} value={form.indirizzo} onChange={(e) => set('indirizzo', e.target.value)} /></div>
      <div style={row2}>
        <div><label style={lbl}>Città</label><input style={inp} value={form.citta} onChange={(e) => set('citta', e.target.value)} /></div>
        <div><label style={lbl}>Provincia</label><input style={inp} value={form.provincia} maxLength={5} onChange={(e) => set('provincia', e.target.value)} placeholder="Es. MI" /></div>
      </div>
      <div style={{ marginBottom: 24 }}><label style={lbl}>Note</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: 70 }} value={form.note} onChange={(e) => set('note', e.target.value)} /></div>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
        <button type="submit" disabled={saving} style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>{saving ? 'Salvataggio…' : 'Salva'}</button>
      </div>
    </form>
  );
}

// Stato ordine → token (usato come accento, niente alpha-concat)
const STATO_ORDINE_META = {
  in_attesa: { label: 'In attesa', color: 'var(--ds-muted)' },
  ordinato:  { label: 'Ordinato',  color: 'var(--zone-blue)' },
  ricevuto:  { label: 'Ricevuto',  color: 'var(--ok)' },
  pagato:    { label: 'Pagato',    color: 'var(--zone-violet)' },
  annullato: { label: 'Annullato', color: 'var(--ds-muted)' },
};
const STATI_ORDINE = Object.keys(STATO_ORDINE_META);
const fmtData = (d) => { if (!d) return '—'; const [y, m, g] = d.split('-'); return `${g}/${m}/${y}`; };

const ORDINE_EMPTY = { descrizione: '', importo: '', aliquota_iva: 22, quantita: '', stato: 'in_attesa', cantiere: '', data_ordine: '', data_consegna_prevista: '', riferimento_ordine: '', note: '' };
const ALIQUOTE_IVA = [{ value: 0, label: 'Esente' }, { value: 4, label: '4%' }, { value: 10, label: '10%' }, { value: 22, label: '22%' }];

function OrdineForm({ initial, cantieriList, onSave, onCancel, saving }) {
  const [form, setForm] = useState({ ...ORDINE_EMPTY, ...initial });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = { width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 3 };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 };
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <div style={{ marginBottom: 12 }}><label style={lbl}>Descrizione *</label>
        <input required style={inp} value={form.descrizione} onChange={(e) => set('descrizione', e.target.value)} placeholder="Es. Acquisto piastrelle 60x60" /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, marginBottom: 12 }}>
        <div><label style={lbl}>Importo imponibile (€) *</label>
          <input required type="number" min="0" step="0.01" style={inp} value={form.importo} onChange={(e) => set('importo', e.target.value)} /></div>
        <div><label style={lbl}>IVA</label>
          <select style={inp} value={form.aliquota_iva} onChange={(e) => set('aliquota_iva', parseInt(e.target.value))}>
            {ALIQUOTE_IVA.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select></div>
        <div><label style={lbl}>Quantità</label>
          <input style={inp} value={form.quantita} onChange={(e) => set('quantita', e.target.value)} placeholder="Es. 50 mq" /></div>
      </div>
      <div style={row2}>
        <div><label style={lbl}>Stato</label>
          <select style={inp} value={form.stato} onChange={(e) => set('stato', e.target.value)}>
            {STATI_ORDINE.map((s) => <option key={s} value={s}>{STATO_ORDINE_META[s].label}</option>)}
          </select></div>
        <div><label style={lbl}>Cantiere</label>
          <select style={inp} value={form.cantiere} onChange={(e) => set('cantiere', e.target.value)}>
            <option value="">— Nessuno —</option>
            {cantieriList.map((c) => <option key={c.id} value={c.id}>{c.titolo}</option>)}
          </select></div>
      </div>
      <div style={row2}>
        <div><label style={lbl}>Data ordine</label>
          <input type="date" style={inp} value={form.data_ordine} onChange={(e) => set('data_ordine', e.target.value)} /></div>
        <div><label style={lbl}>Consegna prevista</label>
          <input type="date" style={inp} value={form.data_consegna_prevista} onChange={(e) => set('data_consegna_prevista', e.target.value)} /></div>
      </div>
      <div style={{ marginBottom: 16 }}><label style={lbl}>Riferimento ordine</label>
        <input style={inp} value={form.riferimento_ordine} onChange={(e) => set('riferimento_ordine', e.target.value)} placeholder="Es. ORD-2026-001" /></div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={{ padding: '8px 16px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Annulla</button>
        <button type="submit" disabled={saving} style={{ padding: '8px 20px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>{saving ? '…' : 'Salva ordine'}</button>
      </div>
    </form>
  );
}

function FornitoreDetail({ fornitore, onClose, onEdit, onDelete }) {
  const { hasFeature } = useAuth();
  const archivioEnabled = hasFeature('archivio_cartelle');
  const lbl = { fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' };
  const val = { fontSize: 14, color: 'var(--ink)', marginTop: 2 };
  const cat = CATEGORIA_META[fornitore.categoria];
  const catColor = cat?.color || 'var(--ds-muted)';

  const [ordiniList, setOrdiniList] = useState([]);
  const [loadingOrdini, setLoadingOrdini] = useState(true);
  const [cantieriList, setCantieriList] = useState([]);
  const [showOrdineForm, setShowOrdineForm] = useState(false);
  const [editingOrdine, setEditingOrdine] = useState(null);
  const [savingOrdine, setSavingOrdine] = useState(false);
  const [spesaTotale, setSpesaTotale] = useState(parseFloat(fornitore.costi_totali) || 0);
  const [showUploadFattura, setShowUploadFattura] = useState(false);
  const [uploadOrdineCtx, setUploadOrdineCtx] = useState(null);

  useEffect(() => {
    ordiniApi.list({ fornitore: fornitore.id }).then((r) => setOrdiniList(r.data.results ?? r.data)).catch(() => {}).finally(() => setLoadingOrdini(false));
    cantieriApi.list().then((r) => setCantieriList(r.data.results ?? r.data)).catch(() => {});
  }, [fornitore.id]);

  const salvaOrdine = async (form) => {
    setSavingOrdine(true);
    try {
      const payload = { ...form, fornitore: fornitore.id };
      if (!payload.cantiere) delete payload.cantiere;
      if (!payload.data_ordine) delete payload.data_ordine;
      if (!payload.data_consegna_prevista) delete payload.data_consegna_prevista;
      const vecchioStato = editingOrdine ? editingOrdine.stato : null;
      let r;
      if (editingOrdine) {
        r = await ordiniApi.update(editingOrdine.id, payload);
        setOrdiniList((l) => l.map((o) => o.id === editingOrdine.id ? r.data : o));
      } else {
        r = await ordiniApi.create(payload);
        setOrdiniList((l) => [r.data, ...l]);
        setSpesaTotale((t) => t + parseFloat(form.importo || 0));
      }
      setShowOrdineForm(false); setEditingOrdine(null);
      // Se stato diventa "pagato", chiedi upload fattura ricevuta
      if (form.stato === 'pagato' && vecchioStato !== 'pagato') {
        setUploadOrdineCtx(r.data);
        setShowUploadFattura(true);
      }
    } catch (e) { alert('Errore: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message)); }
    finally { setSavingOrdine(false); }
  };

  const eliminaOrdine = async (id, importo) => {
    if (!window.confirm('Eliminare questo ordine?')) return;
    try {
      await ordiniApi.delete(id);
      setOrdiniList((l) => l.filter((o) => o.id !== id));
      setSpesaTotale((t) => t - parseFloat(importo || 0));
    } catch { alert('Errore nell\'eliminazione'); }
  };

  return (
    <Modal title={fornitore.nome} onClose={onClose}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <button onClick={() => onEdit(fornitore)} style={{ flex: 1, padding: '9px 0', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Pencil size={15} strokeWidth={2} /> Modifica</button>
        <button onClick={() => { if (window.confirm('Eliminare questo fornitore?')) onDelete(fornitore.id); }} style={{ flex: 1, padding: '9px 0', borderRadius: 'var(--radius-base)', border: `1px solid ${tint('var(--ds-danger)', 35)}`, background: tint('var(--ds-danger)', 12), color: 'var(--ds-danger)', cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Trash2 size={15} strokeWidth={2} /> Elimina</button>
      </div>

      {/* Info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div><div style={lbl}>Categoria</div>
          <div style={{ marginTop: 4 }}><span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: tint(catColor, 14), color: catColor }}>{cat?.label || fornitore.categoria}</span></div>
        </div>
        <div><div style={lbl}>Stato</div><div style={val}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: fornitore.stato === 'attivo' ? 'var(--ok)' : 'var(--ds-muted)' }} />
            {fornitore.stato === 'attivo' ? 'Attivo' : 'Non attivo'}
          </span>
        </div></div>
        <div><div style={lbl}>Email</div><div style={val}>{fornitore.email || '—'}</div></div>
        <div><div style={lbl}>Telefono</div><div style={val}>{fornitore.telefono || '—'}</div></div>
        <div><div style={lbl}>P. IVA</div><div style={val}>{fornitore.piva || '—'}</div></div>
        <div><div style={lbl}>Sito web</div><div style={val}>{fornitore.sito_web ? <a href={fornitore.sito_web} target="_blank" rel="noreferrer" style={{ color: 'var(--brand)' }}>{fornitore.sito_web}</a> : '—'}</div></div>
        <div style={{ gridColumn: '1 / -1' }}><div style={lbl}>Indirizzo</div><div style={val}>{[fornitore.indirizzo, fornitore.citta, fornitore.provincia].filter(Boolean).join(', ') || '—'}</div></div>
        <div><div style={lbl}>Spesa totale</div><div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--ink)', marginTop: 2 }}>{fmtEur(spesaTotale)}</div></div>
        <div><div style={lbl}>Ordini aperti</div><div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: fornitore.ordini_aperti > 0 ? 'var(--warn)' : 'var(--ink)', marginTop: 2 }}>{fornitore.ordini_aperti}</div></div>
        {fornitore.note && <div style={{ gridColumn: '1 / -1', padding: 12, background: 'var(--chip)', borderRadius: 'var(--radius-base)' }}><div style={lbl}>Note</div><div style={{ ...val, whiteSpace: 'pre-wrap' }}>{fornitore.note}</div></div>}
      </div>

      {/* Ordini / Acquisti */}
      <div style={{ borderTop: '1px solid var(--ds-border)', paddingTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={lbl}>Ordini e acquisti</div>
          <button onClick={() => { setEditingOrdine(null); setShowOrdineForm(true); }} style={{ padding: '6px 14px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>+ Nuovo ordine</button>
        </div>

        {showOrdineForm && (
          <div style={{ background: 'var(--chip)', borderRadius: 'var(--radius-base)', padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', marginBottom: 12 }}>{editingOrdine ? 'Modifica ordine' : 'Nuovo ordine'}</div>
            <OrdineForm initial={editingOrdine || {}} cantieriList={cantieriList} onSave={salvaOrdine} onCancel={() => { setShowOrdineForm(false); setEditingOrdine(null); }} saving={savingOrdine} />
          </div>
        )}

        {loadingOrdini ? (
          <div style={{ color: 'var(--ds-muted)', fontSize: 13 }}>Caricamento…</div>
        ) : ordiniList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--ds-muted)', fontSize: 13 }}>Nessun ordine registrato</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ordiniList.map((o) => {
              const sm = STATO_ORDINE_META[o.stato] || { label: o.stato, color: 'var(--ds-muted)' };
              return (
                <div key={o.id} style={{ padding: '10px 14px', borderRadius: 'var(--radius-base)', background: 'var(--chip)', borderLeft: `3px solid ${sm.color}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{o.descrizione}</div>
                      <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 3 }}>
                        <span style={{ color: sm.color, fontWeight: 500 }}>{sm.label}</span>
                        {o.cantiere_titolo && ` · ${o.cantiere_titolo}`}
                        {o.data_ordine && ` · ${fmtData(o.data_ordine)}`}
                        {o.riferimento_ordine && ` · Rif. ${o.riferimento_ordine}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{fmtEur(o.importo)}</div>
                        {o.aliquota_iva != null && <div style={{ fontSize: 10, color: 'var(--ds-muted)' }}>+IVA {o.aliquota_iva}%</div>}
                      </div>
                      <button onClick={() => { setEditingOrdine(o); setShowOrdineForm(true); }} style={{ padding: '3px 8px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 11 }}>Mod.</button>
                      <button onClick={() => eliminaOrdine(o.id, o.importo)} style={{ padding: '3px 8px', borderRadius: 'var(--radius-base)', border: `1px solid ${tint('var(--ds-danger)', 35)}`, background: tint('var(--ds-danger)', 12), color: 'var(--ds-danger)', cursor: 'pointer', fontSize: 11 }}>Elim.</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal upload fattura ricevuta */}
      {showUploadFattura && uploadOrdineCtx && (
        <UploadDocumentoModal
          onClose={() => { setShowUploadFattura(false); setUploadOrdineCtx(null); }}
          onSuccess={() => { setShowUploadFattura(false); setUploadOrdineCtx(null); }}
          cartelle={[]}
          hasArchivio={archivioEnabled}
          defaultFornitore={fornitore.id}
          defaultTipo="fattura"
        />
      )}
    </Modal>
  );
}

function ImportFatturaFornitoreModal({ onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');
  const [confidenza, setConfidenza] = useState('');
  const [cantieriList, setCantieriList] = useState([]);
  const [fornitoriList, setFornitoriList] = useState([]);
  const [cantiere, setCantiere] = useState('');
  const [fornitoreId, setFornitoreId] = useState('');
  const [fornitoreEstratto, setFornitoreEstratto] = useState(null);
  // DBT-157 — il fornitore letto dal PDF ha il nome di uno già presente.
  const [nomeInfo, setNomeInfo] = useState(null);
  const [form, setForm] = useState({ descrizione: '', importo: '', aliquota_iva: 22, riferimento_ordine: '', data_ordine: '', stato: 'ricevuto' });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    cantieriApi.list({ stato: 'in_corso', page_size: 200 }).then((r) => setCantieriList(r.data.results ?? r.data)).catch(() => {});
    fornitoriApi.list({ page_size: 200 }).then((r) => setFornitoriList(r.data.results ?? r.data)).catch(() => {});
  }, []);

  const analizza = async () => {
    if (!file || !cantiere) return;
    setLoading(true);
    setErrore('');
    try {
      const r = await ordiniApi.importaPdf(file);
      const data = r.data;
      const e = data.estratto;
      setConfidenza(data.confidenza);
      setFornitoreEstratto(e.fornitore || null);

      // Match fornitore esistente
      let matchedId = '';
      if (e.fornitore?.nome) {
        // DBT-157 — stessa regola del backend: maiuscole e spazi non contano.
        const match = fornitoriList.find((f) => stessoNome(f.nome, e.fornitore.nome));
        if (match) matchedId = match.id;
      }
      setFornitoreId(matchedId);

      setForm({
        descrizione: e.descrizione || '',
        importo: e.importo ? String(e.importo) : '',
        aliquota_iva: [0, 4, 10, 22].includes(e.aliquota_iva) ? e.aliquota_iva : 22,
        riferimento_ordine: e.numero_documento || '',
        data_ordine: e.data_documento || new Date().toISOString().split('T')[0],
        stato: 'ricevuto',
      });
      setStep(2);
    } catch (err) {
      setErrore(err.response?.data?.detail || 'Errore durante l\'analisi del documento.');
    } finally {
      setLoading(false);
    }
  };

  const registraSpesa = async () => {
    setSaving(true);
    setErrore('');
    try {
      // 1. Se fornitore non trovato, crealo
      let fId = fornitoreId;
      if (!fId && fornitoreEstratto?.nome) {
        const fData = {
          nome: fornitoreEstratto.nome,
          piva: fornitoreEstratto.piva || '',
          indirizzo: fornitoreEstratto.indirizzo || '',
          email: fornitoreEstratto.email || '',
          telefono: fornitoreEstratto.telefono || '',
          citta: fornitoreEstratto.citta || '',
          provincia: fornitoreEstratto.provincia || '',
          categoria: fornitoreEstratto.categoria || 'altro',
        };
        const fr = await fornitoriApi.create(fData);
        fId = fr.data.id;
      }

      // 2. Crea OrdineFornitore (signal crea EventoCantiere tipo=costo automaticamente)
      const payload = {
        ...form,
        importo: parseFloat(form.importo) || 0,
        fornitore: fId || undefined,
        cantiere: cantiere,
      };
      if (!payload.fornitore) delete payload.fornitore;
      if (!payload.data_ordine) delete payload.data_ordine;
      await ordiniApi.create(payload);

      onImported();
      onClose();
    } catch (err) {
      const ng = nomeGiaPresenteDaErrore(err);
      if (ng) { setNomeInfo(ng); return; }
      // FE-003 — messaggio user-friendly, niente dump JSON grezzo della response.
      const detail = err.response?.data;
      const msg = (detail && (detail.detail || detail.error))
        || 'Non è stato possibile registrare l\'ordine. Controlla i dati e riprova.';
      setErrore(msg);
    } finally {
      setSaving(false);
    }
  };

  const imp = parseFloat(form.importo) || 0;
  const ivaCalc = imp * (form.aliquota_iva / 100);
  const totIvato = imp + ivaCalc;

  const inp = { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' };
  const lbl = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4 };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 };
  const CONFIDENZA_META = {
    alta:  { label: 'Confidenza alta',  color: 'var(--ok)' },
    media: { label: 'Confidenza media', color: 'var(--warn)' },
    bassa: { label: 'Confidenza bassa', color: 'var(--ds-danger)' },
  };
  const confMeta = CONFIDENZA_META[confidenza] || CONFIDENZA_META.bassa;

  return (
    <>
    <NomeGiaPresenteModal info={nomeInfo} onClose={() => setNomeInfo(null)} onChiudiModulo={onClose} />
    <Modal title="Importa fattura fornitore da PDF" onClose={onClose}>
      {step === 1 && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <label style={lbl}>Cantiere di destinazione *</label>
            <select style={inp} value={cantiere} onChange={(e) => setCantiere(e.target.value)}>
              <option value="">— Seleziona cantiere —</option>
              {cantieriList.map((c) => <option key={c.id} value={c.id}>{c.titolo}</option>)}
            </select>
            {!cantiere && <div style={{ fontSize: 12, color: 'var(--warn)', marginTop: 4 }}>Seleziona il cantiere prima di caricare il PDF</div>}
          </div>
          <div
            onDragOver={(e) => { e.preventDefault(); if (cantiere) e.currentTarget.style.borderColor = 'var(--brand)'; }}
            onDragLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ds-border)'; }}
            onDrop={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--ds-border)'; if (cantiere) { const f = e.dataTransfer.files[0]; if (f && f.type === 'application/pdf') setFile(f); } }}
            style={{ border: '2px dashed var(--ds-border)', borderRadius: 'var(--radius-card)', padding: '40px 20px', textAlign: 'center', marginBottom: 20, cursor: cantiere ? 'pointer' : 'not-allowed', opacity: cantiere ? 1 : 0.5, transition: 'border-color 0.2s' }}
            onClick={() => { if (cantiere) document.getElementById('import-fatt-pdf-input').click(); }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, color: 'var(--ds-muted)' }}>
              <Upload size={36} strokeWidth={1.5} />
            </div>
            <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500, marginBottom: 4 }}>
              {file ? file.name : 'Trascina un PDF o clicca per selezionare'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>Fatture fornitori, DDT, bolle di consegna</div>
            <input id="import-fatt-pdf-input" type="file" accept=".pdf" style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
          </div>
          {errore && <div style={{ padding: '10px 14px', background: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', borderRadius: 'var(--radius-base)', fontSize: 13, marginBottom: 16 }}>{errore}</div>}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
            <button onClick={analizza} disabled={!file || !cantiere || loading}
              style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: (!file || !cantiere) ? 'var(--ds-border)' : 'var(--brand)', color: '#fff', cursor: (file && cantiere) ? 'pointer' : 'default', fontSize: 14, fontWeight: 500 }}>
              {loading ? 'mAIstro sta leggendo il documento...' : 'Analizza documento →'}
            </button>
          </div>
        </div>
      )}
      {step === 2 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: tint(confMeta.color, 14), color: confMeta.color }}>{confMeta.label}</span>
            <span style={{ fontSize: 13, color: 'var(--ds-muted)' }}>Verifica e correggi i dati estratti</span>
          </div>
          {errore && <div style={{ padding: '10px 14px', background: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', borderRadius: 'var(--radius-base)', fontSize: 13, marginBottom: 16 }}>{errore}</div>}

          {/* Fornitore estratto */}
          {fornitoreEstratto?.nome && !fornitoreId && (
            <div style={{ padding: '10px 14px', background: tint('var(--brand)', 10), borderRadius: 'var(--radius-base)', fontSize: 13, marginBottom: 16, borderLeft: '3px solid var(--brand)' }}>
              Fornitore trovato: <span style={{ fontWeight: 500 }}>{fornitoreEstratto.nome}</span>
              {fornitoreEstratto.piva && <span style={{ color: 'var(--ds-muted)' }}> — P.IVA {fornitoreEstratto.piva}</span>}
              <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>Verra creato automaticamente al salvataggio</div>
            </div>
          )}

          <div style={row2}>
            <div><label style={lbl}>Fornitore esistente</label>
              <select style={inp} value={fornitoreId} onChange={(e) => setFornitoreId(e.target.value)}>
                <option value="">{fornitoreEstratto?.nome ? '— Crea nuovo dal PDF —' : '— Seleziona —'}</option>
                {fornitoriList.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
              </select></div>
            <div><label style={lbl}>N. Documento</label>
              <input style={inp} value={form.riferimento_ordine} onChange={(e) => set('riferimento_ordine', e.target.value)} /></div>
          </div>
          <div style={{ marginBottom: 16 }}><label style={lbl}>Descrizione</label>
            <input style={inp} value={form.descrizione} onChange={(e) => set('descrizione', e.target.value)} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 16, marginBottom: 16 }}>
            <div><label style={lbl}>Importo netto</label>
              <input type="number" min="0" step="0.01" style={inp} value={form.importo} onChange={(e) => set('importo', e.target.value)} /></div>
            <div><label style={lbl}>IVA</label>
              <select style={inp} value={form.aliquota_iva} onChange={(e) => set('aliquota_iva', parseInt(e.target.value))}>
                <option value={0}>Esente</option><option value={4}>4%</option><option value={10}>10%</option><option value={22}>22%</option>
              </select></div>
            <div><label style={lbl}>Totale IVA inclusa</label>
              <div style={{ padding: '9px 12px', fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{fmtEur(totIvato)}</div></div>
          </div>
          <div style={row2}>
            <div><label style={lbl}>Data documento</label>
              <input type="date" style={inp} value={form.data_ordine} onChange={(e) => set('data_ordine', e.target.value)} /></div>
            <div><label style={lbl}>Cantiere</label>
              <div style={{ padding: '9px 12px', fontSize: 14, color: 'var(--ds-muted)' }}>{cantieriList.find((c) => c.id === cantiere)?.titolo || '—'}</div></div>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={() => { setStep(1); setErrore(''); }} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>← Indietro</button>
            <button onClick={registraSpesa} disabled={saving || !form.descrizione || !form.importo}
              style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
              {saving ? 'Registrazione...' : 'Registra spesa'}
            </button>
          </div>
        </div>
      )}
    </Modal>
    </>
  );
}

export default function Fornitori() {
  const isMobile = useIsMobile();
  const { isCards, mode, setMode } = useListMode('fornitori', isMobile);
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rankingFornitori, setRankingFornitori] = useState([]);
  const [search, setSearch] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dettaglio, setDettaglio] = useState(null);
  const [showImportPdf, setShowImportPdf] = useState(false);
  // DBT-157 — creazione o rinomina verso un nome già usato: modale dedicato.
  const [nomeInfo, setNomeInfo] = useState(null);
  const location = useLocation();

  // DBT-157 — /fornitori?apri={id} apre la scheda (da «Apri il fornitore esistente»).
  useEffect(() => {
    const apriId = new URLSearchParams(location.search).get('apri');
    if (apriId) fornitoriApi.get(apriId).then((r) => setDettaglio(r.data)).catch(() => {});
  }, [location.search]);

  useEffect(() => {
    const sorted = [...lista]
      .filter(f => parseFloat(f.costi_totali) > 0)
      .sort((a, b) => parseFloat(b.costi_totali || 0) - parseFloat(a.costi_totali || 0))
      .slice(0, 8);
    const totale = sorted.reduce((s, f) => s + parseFloat(f.costi_totali || 0), 0);
    setRankingFornitori(sorted.map((f, i) => ({
      pos: i + 1,
      nome: f.nome,
      valore: Math.round(parseFloat(f.costi_totali || 0)),
      pct: totale > 0 ? (parseFloat(f.costi_totali || 0) / totale * 100).toFixed(2) : '0',
    })));
  }, [lista]);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filtroCategoria) params.categoria = filtroCategoria;
      const r = await fornitoriApi.list(params);
      setLista(r.data.results ?? r.data);
    } catch {} finally { setLoading(false); }
  }, [search, filtroCategoria]);

  useEffect(() => { carica(); }, [carica]);

  const salva = async (form) => {
    setSaving(true);
    try {
      if (editing) await fornitoriApi.update(editing.id, form);
      else await fornitoriApi.create(form);
      setShowForm(false); setEditing(null); carica();
    } catch (e) {
      const ng = nomeGiaPresenteDaErrore(e);
      if (ng) { setNomeInfo(ng); return; }
      alert('Errore: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    }
    finally { setSaving(false); }
  };

  // DBT-157 — «Apri il fornitore esistente»: chiude il modulo e apre la sua scheda.
  const apriFornitoreEsistente = (id) => {
    setShowForm(false);
    setEditing(null);
    fornitoriApi.get(id).then((r) => setDettaglio(r.data)).catch(() => {});
  };

  const elimina = async (id) => {
    try { await fornitoriApi.delete(id); setDettaglio(null); carica(); }
    catch { alert('Errore nell\'eliminazione'); }
  };

  const stats = {
    totale: lista.length,
    attivi: lista.filter((f) => f.stato === 'attivo').length,
    spesaTotale: lista.reduce((sum, f) => sum + (parseFloat(f.costi_totali) || 0), 0),
    ordiniAperti: lista.reduce((sum, f) => sum + (f.ordini_aperti || 0), 0),
  };

  return (
    <div style={{ padding: '28px 20px 28px 32px', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, color: 'var(--ink)' }}>Fornitori</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--ds-muted)' }}>Gestione fornitori e acquisti</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowImportPdf(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '11px 22px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: 'var(--ds-text)' }}>
            <FileText size={15} strokeWidth={2} />
            Importa fattura PDF
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ padding: '11px 22px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500, boxShadow: '0 4px 14px rgba(46,160,132,0.35)' }}>+ Nuovo fornitore</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Totale', value: stats.totale, color: 'var(--brand)' },
          { label: 'Attivi', value: stats.attivi, color: 'var(--ok)' },
          { label: 'Spesa totale', value: fmtEur(stats.spesaTotale), color: 'var(--ds-danger)', big: true },
          { label: 'Ordini aperti', value: stats.ordiniAperti, color: 'var(--zone-amber)' },
        ].map((s) => (
          <div key={s.label} style={{ background: 'var(--chip)', borderRadius: 'var(--radius-card)', padding: '20px 24px', border: '1px solid var(--ds-border)', borderTop: `3px solid ${s.color}`, boxShadow: CARD_SHADOW }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: s.big ? 18 : 24, fontWeight: 700, color: 'var(--ink)', marginTop: 4 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filtri */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ds-muted)', display: 'flex' }}><Search size={16} strokeWidth={2} /></span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome, città…"
            style={{ width: '100%', padding: '10px 12px 10px 36px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' }} />
        </div>
        <button onClick={() => setFiltroCategoria('')} style={{
          padding: '10px 16px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
          background: !filtroCategoria ? 'var(--brand)' : 'var(--ds-bg)',
          color: !filtroCategoria ? '#fff' : 'var(--ds-text)',
          border: !filtroCategoria ? '1px solid var(--brand)' : '1px solid var(--ds-border)',
        }}>Tutti</button>
        <select value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}
          style={{ padding: '10px 14px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500, cursor: 'pointer', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', outline: 'none', fontFamily: 'inherit' }}>
          <option value="">Filtra categoria…</option>
          {CATEGORIE_LIST.map((c) => <option key={c} value={c}>{CATEGORIA_META[c].label}</option>)}
        </select>
        {!isMobile && (
          <div style={{ marginLeft: 'auto' }}>
            <ListModeToggle mode={mode} setMode={setMode} />
          </div>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--ds-muted)' }}>Caricamento…</div>
      ) : lista.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14, color: 'var(--ds-muted)' }}><Factory size={44} strokeWidth={1.5} /></div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)', marginBottom: 8 }}>Nessun fornitore trovato</div>
          <div style={{ fontSize: 14, color: 'var(--ds-muted)', marginBottom: 24 }}>{search || filtroCategoria ? 'Prova a cambiare i filtri' : 'Aggiungi il tuo primo fornitore'}</div>
          {!search && !filtroCategoria && <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ padding: '11px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>+ Nuovo fornitore</button>}
        </div>
      ) : isCards ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: isMobile ? 10 : 14 }}>
            {lista.map((f) => {
              const cat = CATEGORIA_META[f.categoria];
              const catColor = cat?.color || 'var(--ds-muted)';
              return (
                <DataCard
                  key={f.id}
                  title={f.nome}
                  subtitle={f.citta || null}
                  onClick={() => setDettaglio(f)}
                  badge={
                    <span style={{ padding: '2px 10px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: tint(catColor, 14), color: catColor }}>{cat?.label || f.categoria}</span>
                  }
                  meta={[
                    { label: 'Spesa tot.', value: fmtEur(f.costi_totali) },
                    { label: 'Ordini aperti', value: f.ordini_aperti },
                    f.telefono ? { label: 'Telefono', value: f.telefono } : null,
                    {
                      label: 'Stato',
                      value: (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: f.stato === 'attivo' ? 'var(--ok)' : 'var(--ds-muted)' }} />
                          {f.stato === 'attivo' ? 'Attivo' : 'Non attivo'}
                        </span>
                      ),
                    },
                  ]}
                />
              );
            })}
          </div>
          <RankingTable
            title="Top Fornitori per Spesa"
            colNome="FORNITORE"
            colValore="IMPORTO"
            colCoda="% SUL TOTALE"
            rows={rankingFornitori}
            getSub={(row) => `${Number(row.valore).toLocaleString('it-IT')} €`}
            renderCoda={renderBarraCoda}
          />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '5fr 2fr', gap: 24, alignItems: 'start' }}>
          <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ds-border)' }}>
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>Elenco Fornitori</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--chip)', borderBottom: '2px solid var(--ds-border)' }}>
                {['Fornitore', 'Categoria', 'Email', 'Telefono', 'Città', 'Spesa tot.', 'Ordini', 'Stato'].map((h) => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {lista.map((f, i) => {
                  const cat = CATEGORIA_META[f.categoria];
                  const catColor = cat?.color || 'var(--ds-muted)';
                  return (
                    <tr key={f.id} onClick={() => setDettaglio(f)} style={{ borderTop: '1px solid var(--ds-border)', cursor: 'pointer', background: ROW_BG(i) }}
                      onMouseEnter={(e) => e.currentTarget.style.background = ROW_HOVER}
                      onMouseLeave={(e) => e.currentTarget.style.background = ROW_BG(i)}>
                      <td style={{ padding: '13px 16px', fontWeight: 500, fontSize: 14, color: 'var(--ink)' }}>{f.nome}</td>
                      <td style={{ padding: '13px 16px' }}>
                        <span style={{ padding: '2px 10px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: tint(catColor, 14), color: catColor }}>{cat?.label || f.categoria}</span>
                      </td>
                      <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)' }}>{f.email || <span style={{ color: 'var(--ds-border)' }}>—</span>}</td>
                      <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)' }}>{f.telefono || <span style={{ color: 'var(--ds-border)' }}>—</span>}</td>
                      <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)' }}>{f.citta || <span style={{ color: 'var(--ds-border)' }}>—</span>}</td>
                      <td style={{ padding: '13px 16px', fontSize: 11, fontWeight: 500, color: 'var(--ink)', whiteSpace: 'nowrap' }}>{fmtEur(f.costi_totali)}</td>
                      <td style={{ padding: '13px 16px', fontSize: 11, fontWeight: 500, color: f.ordini_aperti > 0 ? 'var(--warn)' : 'var(--ds-muted)' }}>{f.ordini_aperti}</td>
                      <td style={{ padding: '13px 16px', fontSize: 11, color: f.stato === 'attivo' ? 'var(--ok)' : 'var(--ds-muted)', fontWeight: 500 }}>{f.stato === 'attivo' ? 'Attivo' : 'Non attivo'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <RankingTable
            title="Top Fornitori per Spesa"
            colNome="FORNITORE"
            colValore="IMPORTO"
            colCoda="% SUL TOTALE"
            rows={rankingFornitori}
            getSub={(row) => `${Number(row.valore).toLocaleString('it-IT')} €`}
            renderCoda={renderBarraCoda}
          />
        </div>
      )}

      {showForm && (
        <Modal title={editing ? 'Modifica fornitore' : 'Nuovo fornitore'} onClose={() => { setShowForm(false); setEditing(null); }}>
          <FornitoreForm initial={editing || {}} onSave={salva} onCancel={() => { setShowForm(false); setEditing(null); }} saving={saving} />
        </Modal>
      )}
      <NomeGiaPresenteModal info={nomeInfo} onClose={() => setNomeInfo(null)} onApriEsistente={apriFornitoreEsistente} />
      {dettaglio && (
        <FornitoreDetail fornitore={dettaglio} onClose={() => setDettaglio(null)}
          onEdit={(f) => { setEditing(f); setShowForm(true); setDettaglio(null); }}
          onDelete={elimina} />
      )}
      {showImportPdf && (
        <ImportFatturaFornitoreModal
          onClose={() => setShowImportPdf(false)}
          onImported={() => { carica(); }}
        />
      )}
    </div>
  );
}
